import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { N58MovementDetail, N58Service } from '../../../services/n58.service';

/**
 * Modal de detalle de un movimiento N58. Espejo de
 * [[BancoPlazaTransactionDetailModalComponent]].
 *
 * Recibe el movement completo vía MAT_DIALOG_DATA y lo pinta de inmediato
 * —el listado paginado ya trae todos los campos, incluido `rawDetail`— y
 * además pide `GET /n58/movements/:id`, que trae lo que el listado no tiene:
 * quién es el cliente, quién el beneficiario y qué dice el banco.
 *
 * El orden importa: primero se ve el movimiento, después se enriquece. Si el
 * endpoint falla o tarda, el operador igual está viendo el movimiento —que es
 * como funcionaba esto antes de que el detalle existiera— y el bloque
 * ampliado avisa por su cuenta.
 */
@Component({
  selector: 'app-n58-movement-detail-modal',
  templateUrl: './n58-movement-detail-modal.component.html',
})
export class N58MovementDetailModalComponent implements OnInit {
  movement: any;

  detalle: N58MovementDetail | null = null;
  detalleLoading = false;
  detalleError: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { movement: any },
    private dialogRef: MatDialogRef<N58MovementDetailModalComponent>,
    private n58: N58Service,
  ) {
    this.movement = data?.movement;
  }

  ngOnInit(): void {
    const id = this.movement?.id;
    if (!id) return;

    this.detalleLoading = true;
    this.n58.getMovement(id).subscribe({
      next: (res) => {
        this.detalle = res;
        // El movement que devuelve el detalle es el mismo de la fila pero
        // recién leído de la BD: si la conciliación cambió mientras el
        // listado estaba abierto, acá se ve al día.
        if (res?.movement) this.movement = res.movement;
        this.detalleLoading = false;
      },
      error: (err) => {
        this.detalleError =
          typeof err === 'string'
            ? err
            : 'No se pudo cargar el detalle ampliado';
        this.detalleLoading = false;
      },
    });
  }

  /** Nombre completo del cliente, sin dejar un espacio suelto si falta uno. */
  get clienteNombre(): string {
    const c = this.detalle?.cliente;
    if (!c) return '';
    return [c.nombre, c.apellidos].filter(Boolean).join(' ');
  }

  /**
   * Si hay algo que mostrar en el bloque ampliado. Un cargo del banco no tiene
   * contraparte de ningún tipo, y sin esto el bloque aparecía vacío.
   */
  get hayContraparte(): boolean {
    const d = this.detalle;
    if (!d) return false;
    return !!(
      d.contraparte?.nombre ||
      d.contraparte?.cuenta ||
      d.beneficiario ||
      d.bancoDice ||
      d.referenciaVes ||
      d.cliente
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  getTypeLabel(type: string): string {
    return {
      payin: 'Payin',
      payout: 'Payout',
      cargo: 'Cargo bancario',
      // Sin esta entrada la etiqueta caía al valor crudo del backend y el
      // modal mostraba "reintegro" en minúscula, sin estilo.
      reintegro: 'Reintegro',
    }[type] || type;
  }

  getTypeClass(type: string): string {
    return {
      payin: 'status-payin',
      payout: 'status-payout',
      cargo: 'status-cargo',
      // Reusa el estilo de payin porque es plata que entra.
      reintegro: 'status-payin',
    }[type] || '';
  }

  /**
   * Un reintegro es el banco devolviendo un payout que no se completó: entra
   * plata. El `payin ? '+' : '-'` que había acá le ponía signo negativo.
   */
  getAmountSign(type: string): string {
    return type === 'payin' || type === 'reintegro' ? '+' : '-';
  }

  getStatusLabel(status: string): string {
    return {
      registered: 'Registrado',
      processed: 'Procesado',
      failed: 'Fallido',
    }[status] || status;
  }

  getConciliationLabel(status: string): string {
    return {
      UNMATCHED: 'Sin conciliar',
      MATCHED: 'En conciliación',
      RECONCILED: 'Conciliado',
    }[status] || status;
  }

  getConciliationClass(status: string): string {
    return {
      UNMATCHED: 'concil-unmatched',
      MATCHED: 'concil-matched',
      RECONCILED: 'concil-reconciled',
    }[status] || '';
  }
}
